import React, { useState, useEffect, useRef, useMemo } from 'react';
import { isDevice } from '../../helpers';
import { v4 as uuidv4 } from 'uuid';

const Dropdown = ({
  label,
  placeholder = 'Dropdown',
  selectedOption = null,
  options = [
    {
      value: 'us',
      label: 'US',
      placeholder: 'Enter City, State or Zip',
      source: 'Production US - English'
    },
    {
      value: 'ca',
      label: 'CA',
      default: 'true',
      placeholder: 'Enter City, Province or Postal Code',
      source: 'Production CA'
    }
  ],
  onChange = () => {},
  disabled = false,
  compareAttr = 'value',
  innerLabel,
  addPlaceholderOption = false,
  style
}) => {
  const [open, toggle] = useState(false);
  const [cursor, setCursor] = useState(null);
  const guid = useRef(uuidv4());
  const listboxGuid = useRef(uuidv4());
  const nativeEl = useRef(null);
  const dropdownEl = useRef(null);
  const triggerEl = useRef(null);
  const listingEl = useRef(null);

  useEffect(() => {
    const handleClickOutside = e => {
      const container = dropdownEl.current;

      if (container) {
        if (container !== e.target && !container.contains(e.target)) {
          toggle(false);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (open) {
      listingEl.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (selectedOption) {
      const index = options.findIndex(
        option => option[compareAttr] === selectedOption[compareAttr]
      );
      setCursor(index);
    } else {
      setCursor(null);
    }
  }, [compareAttr, options, selectedOption]);

  const handleTriggerClick = e => {
    if (isDevice()) {
      return;
    }

    toggle(!open);
  };

  const handleOptionClick = option => {
    if (option === null) {
      setCursor(null);
      onChange(null);
      toggle(false);
      return;
    }
    onChange(option);
    toggle(false);
  };

  const handleOptionChange = e => {
    const option = options.find(option => option.value === e.target.value);
    onChange(option);
    toggle(false);
  };

  const handleKeyDown = e => {
    /* eslint-disable indent */
    switch (e.keyCode) {
      case 38: // up
        e.preventDefault();
        if (cursor) {
          setCursor(cursor - 1);
        } else {
          setCursor(options.length - 1);
        }
        break;
      case 40: // down
        e.preventDefault();
        if (cursor !== null && cursor < options.length - 1) {
          setCursor(cursor + 1);
        } else {
          setCursor(0);
        }
        break;
      case 27: // esc
        e.preventDefault();
        toggle(false);
        triggerEl.current.focus();
        break;
      case 13: // enter
        e.preventDefault();
        if (cursor !== null) {
          onChange(options[cursor]);
        }
        toggle(false);
        triggerEl.current.focus();
        break;
      case 9: // tab
        toggle(false);
        break;
    }
    /* eslint-enable indent */
  };

  const getDisplayLabel = () => {
    if (selectedOption) {
      if (innerLabel) {
        return `${
          selectedOption.image
            ? `<img class="dropdown-image" src='${selectedOption.image}'>`
            : ''
        }${innerLabel} ${selectedOption.label}`;
      }
      return `${
        selectedOption.image
          ? `<img class="dropdown-image" src='${selectedOption.image}'>`
          : ''
      }${selectedOption.label}`;
    }
    return placeholder || null;
  };

  return (
    <div className="form-group form-group--dropdown form-group--dropdown-outlined">
      <label htmlFor={guid.current} className="form-label">
        {label}
      </label>
      <div
        className={`dropdown${isDevice() ? ' dropdown--native' : ''}${
          style ? ` dropdown--${style}` : ''
        }`}
        ref={dropdownEl}
      >
        <select
          id={guid.current}
          onChange={handleOptionChange}
          value={selectedOption ? selectedOption.Value : ''}
          tabIndex="-1"
          aria-label={`${label || ''} ${getDisplayLabel()}`}
          ref={nativeEl}
        >
          {cursor !== null && addPlaceholderOption && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option, i) => {
            return (
              <option key={i} value={option.value}>
                {typeof option === 'object' ? option.label : option}
              </option>
            );
          })}
        </select>
        <button
          className="dropdown__trigger field__select"
          role="combobox"
          aria-label={`${label || ''} ${getDisplayLabel()}`}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxGuid.current}
          onClick={handleTriggerClick}
          disabled={disabled}
          ref={triggerEl}
        >
          <div
            style={{ display: 'flex', alignItems: 'center' }}
            dangerouslySetInnerHTML={{ __html: getDisplayLabel() }}
          ></div>
        </button>
        <ul
          role="listbox"
          id={listboxGuid.current}
          aria-activedescendant={
            cursor !== null ? `${guid.current}-${cursor}` : null
          }
          tabIndex="-1"
          className="dropdown__list"
          ref={listingEl}
          onKeyDown={handleKeyDown}
        >
          {cursor !== null && addPlaceholderOption && (
            <button
              className="dropdown__item field__select"
              role="option"
              aria-selected={cursor === -1}
              onClick={() => handleOptionClick(null)}
            >
              {placeholder}
            </button>
          )}
          {options.map((option, i) => (
            <button
              role="option"
              key={i}
              id={`${guid.current}-${i}`}
              onClick={() => handleOptionClick(option)}
              className="dropdown__item field__select"
              aria-selected={cursor === i}
            >
              {option.image && (
                <img className="dropdown-image" src={option.image} />
              )}
              {typeof option === 'object' ? option.label : option}
            </button>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Dropdown;
